import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQuery, getBaseQueryWithReauth } from '../../redux/baseQueryAuth';
import type { TSettlementModel } from '../../redux/types/SettlementsTypes';

export type CurrencyRate = {
  code: string;
  name: string;
  order_rate: string | null;
  withdrawal_rate: string | null;
  order_source: string | null;
  withdrawal_source: string | null;
  orders_enabled: boolean;
  withdrawals_enabled: boolean;
};

export type MerchantDashboard = {
  merchant: { id: number; username: string; callback_url: string | null };
  balances: {
    available_to_merchant: string;
    available_to_platform: string;
    currency: 'USDT';
  };
  activity: { deposits_total: number; withdrawals_total: number; open_disputes: number };
  currency_rates: CurrencyRate[];
};

export type Page<T> = {
  count: number;
  page?: number;
  page_size?: number;
  results: T[];
};

export type MerchantApplication = {
  id: string;
  merchant_order_id: string | null;
  merchant_client_id: string | null;
  create_dt: string;
  type: 'deposit' | 'withdrawal';
  currency: string;
  amount: string;
  final_amount: string;
  course: string;
  commission_usdt: string | null;
  status: string;
  bank: string;
  has_files: boolean;
  comments: string[];
  disputes: string[];
};

export type MerchantDispute = {
  id: number;
  order: string | null;
  withdrawal: string | null;
  create_dt: string;
  modify_dt: string;
  comment: string;
  summa: string;
  status: string;
  currency: string;
  application_type: 'deposit' | 'withdrawal';
};

export type ApplicationsQuery = {
  page?: number;
  page_size?: number;
  type?: 'all' | 'deposit' | 'withdrawal';
  status?: string;
  currency?: string;
  search?: string;
};

export type DisputesQuery = {
  page?: number;
  page_size?: number;
  state?: 'all' | 'open' | 'resolved';
  search?: string;
};

export type SettlementsQuery = { page?: number; page_size?: number };

export const merchantCabinetApi = createApi({
  reducerPath: 'merchantCabinetApi',
  baseQuery: getBaseQueryWithReauth(baseQuery),
  tagTypes: ['Dashboard', 'Applications', 'Disputes', 'Settlements'],
  endpoints: (build) => ({
    getDashboard: build.query<MerchantDashboard, void>({
      query: () => 'merchant/dashboard/',
      providesTags: ['Dashboard'],
    }),
    getApplications: build.query<Page<MerchantApplication>, ApplicationsQuery>({
      query: (params) => ({ url: 'merchant/applications/', params }),
      providesTags: ['Applications'],
    }),
    getDisputes: build.query<Page<MerchantDispute>, DisputesQuery>({
      query: (params) => ({ url: 'disputes/merchant/', params }),
      providesTags: ['Disputes'],
    }),
    getSettlements: build.query<Page<TSettlementModel>, SettlementsQuery>({
      query: (params) => ({ url: 'merchant/settlements/', params }),
      providesTags: ['Settlements'],
    }),
  }),
});

export const {
  useGetDashboardQuery,
  useGetApplicationsQuery,
  useGetDisputesQuery,
  useGetSettlementsQuery,
} = merchantCabinetApi;
